import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { SearchOutlined } from '@mui/icons-material';
import { Button, InputAdornment, Slide, Stack, Typography } from '@mui/material';

import { fetchOrgMembers, setContextualSupport, unsetContextualSupport } from 'js/actions';
import { GET_ORG_MEMBERS_REQUEST } from 'js/constants/actionTypes';
import * as PATHS from 'js/constants/pathnames';
import { SUPPORT_KEY_ACCOUNT } from 'js/constants/supportPageKeys';
import useAppDispatch from 'js/hooks/useAppDispatch';
import UIUtils from 'js/lib/UIUtils';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';

import { AddUser } from 'new/components/settings/Members/AddUser';
import { columns } from 'new/components/settings/Members/columns';
import { DeactivateUsers } from 'new/components/settings/Members/DeactivateUsers';
import { EditUser } from 'new/components/settings/Members/EditUser';
import { MemberActions } from 'new/components/settings/Members/MemberActions';
import { Box } from 'new/elements/Box';
import { ChipBadge } from 'new/elements/ChipBadge';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { Table } from 'new/elements/Table';
import { TextField } from 'new/elements/TextField';
import { User } from 'new/entities/User';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';
import { useDistance } from 'new/hooks/useDistance';
import { useModals } from 'new/hooks/useModals';
import { filterBy } from 'new/utils/filterBy';

import styles from './Members.module.scss';

function Search({ onSearchChange }) {
  return (
    <TextField
      className={styles.search}
      placeholder="Find Members by name or email"
      onChange={onSearchChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlined />
          </InputAdornment>
        ),
      }}
    />
  );
}

export function Members() {
  const [selectedMembers, setSelectedMembers] = useState([]);
  const org = useCurrentOrg();
  const user = useCurrentUser();
  const { openModal } = useModals();
  const ref = useRef();
  const dispatch = useAppDispatch();

  const loading = useSelector((state: any) => state.request[GET_ORG_MEMBERS_REQUEST]);
  const distance = useDistance(ref, 500);

  const memberArray = useOrgMembers() as User[];
  const [searchQuery, setSearchQuery] = useState('');

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const globallyFilteredMembers = useMemo(() => {
    if (!searchQuery) return memberArray;
    return memberArray.filter(filterBy(searchQuery, 'full_name', 'email'));
  }, [memberArray, searchQuery]);

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_ACCOUNT));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_ACCOUNT));
    };
  }, [dispatch]);

  useEffect(() => {
    if (org.id) {
      dispatch(fetchOrgMembers(org.id));
    }
  }, [org.id]);

  const onSelectionChange = (members) => {
    setSelectedMembers(members);
  };

  const onClickDeactivate = (deactivate) => () => {
    openModal(DeactivateUsers.modalConfig.name, {
      deactivate,
      members: selectedMembers,
    });
  };

  function AddUserView() {
    const { openModal } = useModals();

    const onClick = () => {
      openModal(AddUser.modalConfig.name);
    };

    return (
      <Button
        onClick={onClick}
        variant="contained"
        className={styles.addMemberButton}
        startIcon={<PlusIcon />}
      >
        New Member
      </Button>
    );
  }

  const allOrgActive = selectedMembers.every((m) => m?.org_membership_status === 'ACTIVE');
  const allOrgDeactivated = selectedMembers.every(
    (m) => m?.org_membership_status === 'DEACTIVATED',
  );
  const canDeactivate = allOrgActive || allOrgDeactivated;

  return (
    <PageLayout
      ref={ref}
      title="Members"
      className={styles.container}
      titleAccessory={
        loading ? null : (
          <ChipBadge
            color="disabled"
            label={`${UIUtils.toPluralFormat('member', memberArray.length)}`}
          />
        )
      }
      bottomView={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          width="100%"
          marginTop={3}
        >
          <Search onSearchChange={onSearchChange} />
          {user.isAdmin ? <AddUserView /> : null}
        </Stack>
      }
    >
      <Table
        height={distance}
        columns={columns}
        getRowClassName={(row) =>
          row?.org_membership_status !== 'ACTIVE' ? styles.rowDeactivated : ''
        }
        rows={globallyFilteredMembers}
        loading={loading}
        rowEndRender={MemberActions}
        onSelectedRowsChange={user?.isAdmin ? onSelectionChange : undefined}
        isRowSelectable={(row) =>
          org?.owner == null || row?.id !== org.owner || row?.email !== 'admin@nexla.com'
        }
      />
      <Slide direction="up" in={selectedMembers?.length > 0}>
        <Box className={styles.memberContextContainer}>
          <div className={styles.memberContextContent}>
            <Typography variant="button">{selectedMembers?.length} Users Selected</Typography>
            <div className={styles.memberContextActions}>
              {canDeactivate && (
                <Button onClick={onClickDeactivate(allOrgActive)} variant="outlined">
                  {allOrgDeactivated ? 'Activate' : 'Deactivate'}
                </Button>
              )}
            </div>
          </div>
        </Box>
      </Slide>
    </PageLayout>
  );
}

Members.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/members`,
  screenName: 'OrgMembers',
  modals: [AddUser, EditUser, DeactivateUsers],
  header: {
    title: 'Settings',
    slugTitle: 'Members',
  },
  domain: 'settings',
};
